// src/components/ui.jsx — Composants UI réutilisables

export const fmt = n =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0)

export const fmtDate = d =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '—'

export const initials = n =>
  (n || '').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()

export const TYPES = {
  devis: 'Devis', facture: 'Facture',
  bon_commande: 'Bon de commande', bon_livraison: 'Bon de livraison', avoir: 'Avoir',
}

export const STATUTS = {
  brouillon: 'Brouillon', envoye: 'Envoyé', accepte: 'Accepté',
  refuse: 'Refusé', en_attente: 'En attente', partiel: 'Partiel',
  regle: 'Réglé', annule: 'Annulé', transforme: 'Transformé',
}

// ── Badge statut ──────────────────────────────────────────────
const DOT = { regle:'#2e7d52', en_attente:'#e88c00', partiel:'#e88c00', envoye:'#1565c0', brouillon:'#868e96', refuse:'#dc3545', annule:'#dc3545', accepte:'#2e7d52', transforme:'#6a1b9a' }

export function BadgeStatut({ statut }) {
  return (
    <span className={`badge-statut s-${statut}`}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: DOT[statut] || '#868e96', display: 'inline-block' }} />
      {STATUTS[statut] || statut}
    </span>
  )
}

// ── Badge type document ───────────────────────────────────────
export function BadgeType({ type }) {
  return <span className={`doc-type t-${type}`}>{TYPES[type] || type}</span>
}

// ── Stat Card ─────────────────────────────────────────────────
export function StatCard({ label, value, icon, trend, color = 'primary' }) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="stat-lbl">{label}</span>
        <i className={`ti ti-${icon} stat-icon`} style={{ color: color === 'danger' ? 'var(--danger)' : 'var(--primary)' }} />
      </div>
      <div className="stat-val">{value}</div>
      {trend && (
        <div className={`stat-trend ${trend.up ? 'up' : 'down'}`}>
          <i className={`ti ti-trend-${trend.up ? 'up' : 'down-2'}`} />
          {trend.label}
        </div>
      )}
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────────
export function Modal({ title, onClose, children, footer, large = false }) {
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal${large ? ' modal-lg' : ''}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn sm icon" onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

// ── Confirm delete button ──────────────────────────────────────
export function DeleteBtn({ onConfirm, label = '' }) {
  const handleClick = () => {
    if (confirm(`Supprimer ${label ? `"${label}"` : 'cet élément'} ?`)) onConfirm()
  }
  return (
    <button className="btn sm" onClick={handleClick} title="Supprimer">
      <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
    </button>
  )
}

// ── Empty state ────────────────────────────────────────────────
export function Empty({ icon = 'inbox', message = 'Aucun élément' }) {
  return (
    <div className="empty">
      <i className={`ti ti-${icon}`} />
      <p>{message}</p>
    </div>
  )
}

// ── Loading spinner ────────────────────────────────────────────
export function Loading({ message = 'Chargement...' }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>
      <i className="ti ti-loader-2" style={{ fontSize: 28, display: 'block', marginBottom: 8, animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      {message}
    </div>
  )
}

// ── Avatar ─────────────────────────────────────────────────────
export function Avatar({ name, size = 34 }) {
  return (
    <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.35 }}>
      {initials(name)}
    </div>
  )
}

// ── Form helpers ───────────────────────────────────────────────
export function FormGroup({ label, required, children }) {
  return (
    <div className="form-group">
      {label && <label className={`form-label${required ? ' req' : ''}`}>{label}</label>}
      {children}
    </div>
  )
}

export function Pagination({ page, lastPage, onChange }) {
  const pages = []

  // Toujours afficher : première, dernière, courante ±2
  const range = new Set([1, lastPage])
  for (let i = Math.max(1, page - 2); i <= Math.min(lastPage, page + 2); i++) range.add(i)
  const sorted = [...range].sort((a, b) => a - b)

  // Construire avec ellipses
  let prev = 0
  sorted.forEach(p => {
    if (prev && p - prev > 1) pages.push('...')
    pages.push(p)
    prev = p
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '14px 0 4px' }}>
      <button
        className="page-btn"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <i className="ti ti-chevron-left" />
      </button>

      {pages.map((p, i) =>
        p === '...'
          ? <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--text3)', fontSize: 13 }}>…</span>
          : <button
              key={p}
              className={`page-btn${p === page ? ' active' : ''}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
      )}

      <button
        className="page-btn"
        disabled={page >= lastPage}
        onClick={() => onChange(page + 1)}
      >
        <i className="ti ti-chevron-right" />
      </button>

      <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>
        Page {page} / {lastPage}
      </span>
    </div>
  )
}